import request, { USE_MOCK } from './request'
import {
  mockLogin,
  mockRequest,
  dashboardStats,
  mockUsers,
  mockPlans,
  mockOrders,
  mockRechargeRecords,
  rechargeOptions,
  mockBanners,
  recentOrders,
  mockGlobalAlgorithm,
  mockPlanAlgorithms
} from '@/mock/data'

export function login(data) {
  if (USE_MOCK) return mockLogin(data.username, data.password)
  return request.post('/admin/login', data)
}

export function changeMyPassword(data) {
  if (USE_MOCK) return mockRequest(null)
  return request.put('/admin/profile/password', data)
}

export function getAdmins() {
  if (USE_MOCK) {
    return mockRequest({
      list: [
        { id: 1, username: 'admin', nickname: '超级管理员', role: 'super', status: 'active', lastLogin: '2026-06-23 10:00:00' }
      ]
    })
  }
  return request.get('/admin/admins')
}

export function resetAdminPassword(id, newPassword) {
  if (USE_MOCK) return mockRequest(null)
  return request.put(`/admin/admins/${id}/password`, { newPassword })
}

export function getDashboard() {
  if (USE_MOCK) {
    return mockRequest({
      stats: dashboardStats,
      recentOrders
    })
  }
  return request.get('/admin/dashboard')
}

export function getUsers(params) {
  if (USE_MOCK) {
    let list = [...mockUsers]
    if (params && params.keyword) {
      const kw = params.keyword.trim()
      list = list.filter(u => u.phone.includes(kw) || u.nickname.includes(kw))
    }
    if (params && params.status) {
      list = list.filter(u => u.status === params.status)
    }
    return mockRequest({ list, total: list.length })
  }
  return request.get('/admin/users', { params })
}

export function createUser(data) {
  if (USE_MOCK) {
    const id = mockUsers.length ? Math.max(...mockUsers.map(u => u.id)) + 1 : 10001
    const user = {
      id,
      username: data.username,
      nickname: data.nickname || data.username,
      phone: data.phone || '',
      balance: 0,
      vipLevel: 0,
      status: 'active',
      agentStatus: 'none',
      agentLevel: 0,
      registerTime: new Date().toISOString().slice(0, 19).replace('T', ' '),
      lastLogin: null
    }
    mockUsers.unshift(user)
    return mockRequest(user)
  }
  return request.post('/admin/users', data)
}

export function updateUserStatus(id, status) {
  if (USE_MOCK) {
    const user = mockUsers.find(u => u.id === id)
    if (user) user.status = status
    return mockRequest(null)
  }
  return request.put(`/admin/users/${id}/status`, { status })
}

export function updateUserAgent(id, data) {
  if (USE_MOCK) {
    const user = mockUsers.find(u => u.id === id)
    if (user) {
      user.agentStatus = data.agentStatus
      user.agentLevel = data.agentStatus === 'active' ? data.agentLevel || 1 : 0
      if (data.agentStatus === 'active' && !user.agentCode) {
        user.agentCode = `AC${Math.floor(100000 + Math.random() * 900000)}`
      }
    }
    return mockRequest({
      agentStatus: user?.agentStatus,
      agentLevel: user?.agentLevel,
      agentCode: user?.agentCode
    })
  }
  return request.put(`/admin/users/${id}/agent`, data)
}

export function resetUserPassword(id, newPassword) {
  if (USE_MOCK) return mockRequest(null)
  return request.put(`/admin/users/${id}/password`, { newPassword })
}

export function rechargeUser(id, data) {
  if (USE_MOCK) {
    const user = mockUsers.find(u => u.id === id)
    if (user) {
      const total = Number(data.amount || 0) + Number(data.bonus || 0)
      user.balance = Number(user.balance || 0) + total
    }
    return mockRequest({ balance: user?.balance })
  }
  return request.post(`/admin/users/${id}/recharge`, data)
}

export function adjustUserBalance(id, data) {
  if (USE_MOCK) {
    const user = mockUsers.find(u => u.id === id)
    const oldBalance = Number(user?.balance || 0)
    const balance = Number(data.balance || 0)
    if (user) user.balance = balance
    return mockRequest({ oldBalance, balance, delta: balance - oldBalance })
  }
  return request.put(`/admin/users/${id}/balance`, data)
}

export function getPlans() {
  if (USE_MOCK) return mockRequest({ list: mockPlans })
  return request.get('/admin/plans')
}

export function updatePlan(id, data) {
  if (USE_MOCK) {
    const plan = mockPlans.find(p => p.id === id)
    if (plan) Object.assign(plan, data)
    return mockRequest(plan)
  }
  return request.put(`/admin/plans/${id}`, data)
}

export function getOrders(params) {
  if (USE_MOCK) {
    let list = [...mockOrders]
    if (params && params.status) list = list.filter(o => o.status === params.status)
    if (params && params.keyword) {
      const kw = params.keyword.trim()
      list = list.filter(o => o.id.includes(kw) || o.userPhone.includes(kw))
    }
    return mockRequest({ list, total: list.length })
  }
  return request.get('/admin/orders', { params })
}

export function getRechargeRecords(params) {
  if (USE_MOCK) {
    let list = [...mockRechargeRecords]
    if (params && params.status) list = list.filter(r => r.status === params.status)
    if (params && params.startDate) {
      list = list.filter(r => String(r.createTime || '').slice(0, 10) >= params.startDate)
    }
    if (params && params.endDate) {
      list = list.filter(r => String(r.createTime || '').slice(0, 10) <= params.endDate)
    }
    const totalAmount = list
      .filter(r => r.status === 'success')
      .reduce((sum, r) => sum + Number(r.amount || 0) + Number(r.bonus || 0), 0)
    return mockRequest({ list, total: list.length, totalAmount })
  }
  return request.get('/admin/recharge/records', { params })
}

export function getRechargeOptions() {
  if (USE_MOCK) return mockRequest({ list: rechargeOptions })
  return request.get('/admin/recharge/options')
}

export function updateRechargeOptions(options) {
  if (USE_MOCK) {
    rechargeOptions.splice(0, rechargeOptions.length, ...options.map((item, index) => ({
      id: item.id || index + 1,
      amount: item.amount,
      bonus: item.bonus,
      sort: item.sort || index + 1
    })))
    return mockRequest({ list: rechargeOptions })
  }
  return request.put('/admin/recharge/options', { options })
}

export function getBanners() {
  if (USE_MOCK) return mockRequest({ list: mockBanners })
  return request.get('/admin/banners')
}

export function updateBanner(id, data) {
  if (USE_MOCK) {
    const banner = mockBanners.find(b => b.id === id)
    if (banner) Object.assign(banner, data)
    return mockRequest(banner)
  }
  return request.put(`/admin/banners/${id}`, data)
}

export function getAnnouncements() {
  return request.get('/admin/announcements')
}

export function createAnnouncement(data) {
  return request.post('/admin/announcements', data)
}

export function updateAnnouncement(id, data) {
  return request.put(`/admin/announcements/${id}`, data)
}

export function deleteAnnouncement(id) {
  return request.delete(`/admin/announcements/${id}`)
}

export function getMessages(params) {
  return request.get('/admin/messages', { params })
}

export function sendMessage(data) {
  return request.post('/admin/messages', data)
}

export function getAppVersions() {
  return request.get('/admin/app-versions')
}

export function createAppVersion(data) {
  return request.post('/admin/app-versions', data)
}

export function updateAppVersion(id, data) {
  return request.put(`/admin/app-versions/${id}`, data)
}

export function deleteAppVersion(id) {
  return request.delete(`/admin/app-versions/${id}`)
}

export function uploadAppPackage(file) {
  const formData = new FormData()
  formData.append('file', file)
  return request.post('/admin/app-versions/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000
  })
}

export function getCommissionAgents(params) {
  return request.get('/admin/commissions/agents', { params })
}

export function getAgentCommissionDetails(agentUserId, params) {
  return request.get(`/admin/commissions/agents/${agentUserId}`, { params })
}

export function approveAgentCommissions(agentUserId, reviewNote) {
  return request.put(`/admin/commissions/agents/${agentUserId}/approve-all`, { reviewNote: reviewNote || '' })
}

export function rejectAgentCommissions(agentUserId, reviewNote) {
  return request.put(`/admin/commissions/agents/${agentUserId}/reject-all`, { reviewNote: reviewNote || '' })
}

export function getCommissions(params) {
  return request.get('/admin/commissions', { params })
}

export function approveCommission(id, reviewNote) {
  return request.put(`/admin/commissions/${id}/approve`, { reviewNote: reviewNote || '' })
}

export function rejectCommission(id, reviewNote) {
  return request.put(`/admin/commissions/${id}/reject`, { reviewNote: reviewNote || '' })
}

export function getWithdrawals(params) {
  return request.get('/admin/withdrawals', { params })
}

export function approveWithdrawal(id, reviewNote) {
  return request.put(`/admin/withdrawals/${id}/approve`, { reviewNote: reviewNote || '' })
}

export function rejectWithdrawal(id, reviewNote) {
  return request.put(`/admin/withdrawals/${id}/reject`, { reviewNote: reviewNote || '' })
}

export function getAgentApplications(params) {
  return request.get('/admin/agent-applications', { params })
}

export function approveAgentApplication(id, data) {
  return request.put(`/admin/agent-applications/${id}/approve`, {
    agentLevel: data.agentLevel || 1,
    reviewNote: data.reviewNote || ''
  })
}

export function rejectAgentApplication(id, reviewNote) {
  return request.put(`/admin/agent-applications/${id}/reject`, { reviewNote: reviewNote || '' })
}

const mockSecurityQuestions = {
  question1: '您的出生地是哪里？',
  question2: '您最喜欢的食物是什么？',
  question3: '您的幸运数字是？'
}

export function getSecurityQuestions() {
  if (USE_MOCK) return mockRequest({ ...mockSecurityQuestions })
  return request.get('/admin/security-questions')
}

export function updateSecurityQuestions(data) {
  if (USE_MOCK) {
    Object.assign(mockSecurityQuestions, data)
    return mockRequest({ ...mockSecurityQuestions })
  }
  return request.put('/admin/security-questions', data)
}

const mockContactConfig = {
  title: '联系我们',
  content: '如有疑问请添加客服微信或拨打客服电话',
  wechat: 'anchao_service',
  phone: '400-000-0000',
  workTime: '工作日 9:00-18:00',
  qrImageUrl: '',
  status: 'online'
}

export function getContactConfig() {
  if (USE_MOCK) return mockRequest({ ...mockContactConfig })
  return request.get('/admin/contact')
}

export function updateContactConfig(data) {
  if (USE_MOCK) {
    Object.assign(mockContactConfig, data)
    return mockRequest({ ...mockContactConfig })
  }
  return request.put('/admin/contact', data)
}

export function uploadContactImage(file) {
  const formData = new FormData()
  formData.append('file', file)
  return request.post('/admin/contact/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000
  })
}

export function getPlanAlgorithms() {
  if (USE_MOCK) {
    return mockRequest({
      global: { ...mockGlobalAlgorithm },
      list: mockPlanAlgorithms.map(item => ({ ...item }))
    })
  }
  return request.get('/admin/plan-algorithms')
}

export function updateGlobalAlgorithm(data) {
  if (USE_MOCK) {
    const { dingweiAlgo, ...rest } = data || {}
    Object.assign(mockGlobalAlgorithm, rest)
    if (dingweiAlgo) {
      mockGlobalAlgorithm.dingweiAlgo = { ...mockGlobalAlgorithm.dingweiAlgo, ...dingweiAlgo }
    }
    return mockRequest({ ...mockGlobalAlgorithm })
  }
  return request.put('/admin/plan-algorithms/global', data)
}

export function updatePlanAlgorithm(id, data) {
  if (USE_MOCK) {
    const item = mockPlanAlgorithms.find(p => p.id === id)
    if (item) {
      Object.assign(item, data, { updateTime: new Date().toISOString().slice(0, 19).replace('T', ' ') })
    }
    return mockRequest(item)
  }
  return request.put(`/admin/plan-algorithms/${id}`, data)
}

export function getPlanAlgorithmBacktest() {
  if (USE_MOCK) {
    return mockRequest({
      historyCount: 1000,
      latestIssue: 20260623001,
      generateMode: 'history_predict',
      results: [
        {
          planId: 1,
          planName: '计划A · 稳胆系列',
          strategy: '稳胆·多维热号',
          testPeriods: 200,
          historyUsed: 1000,
          algorithms: ['全号频率', '近100期热号', '百十个位频', '和值走势', '马尔可夫链'],
          singleDadi: { exact: 82.5, tol1: 91.0, tol2: 96.5 },
          union: { exact: 98.0, tol2: 99.5 }
        },
        {
          planId: 2,
          planName: '计划B · 冲击系列',
          strategy: '冲击·遗漏反趋势',
          testPeriods: 200,
          historyUsed: 1000,
          algorithms: ['遗漏回补', '跨度形态', '奇偶平衡', '对子豹子', '反趋势选号'],
          singleDadi: { exact: 80.0, tol1: 89.5, tol2: 95.0 },
          union: { exact: 97.5, tol2: 99.0 }
        }
      ]
    })
  }
  return request.get('/admin/plan-algorithms/backtest')
}

export function getPlanAlgorithmHitStats(params = {}) {
  if (USE_MOCK) {
    return mockRequest({
      limit: 30,
      results: [
        {
          id: 1,
          name: '计划A · 稳胆系列',
          dadiStats: [
            { dadiIndex: 0, label: '算法主大底(交集)', exactHitRate: 85, toleranceHitRate: 98, consecutiveExact: 3, consecutiveTolerance: 5 },
            ...Array.from({ length: 10 }, (_, i) => ({
              dadiIndex: i + 1,
              label: `大底${String(i + 1).padStart(2, '0')}`,
              exactHitRate: 82 + (i % 3),
              toleranceHitRate: 96 + (i % 2),
              consecutiveExact: i % 4,
              consecutiveTolerance: i % 6
            }))
          ]
        }
      ]
    })
  }
  return request.get('/admin/plan-algorithms/hit-stats', { params })
}

export function getDadiPublishStatus() {
  return request.get('/admin/plan-algorithms/dadi-publish-status')
}

export function publishPlanDadi(force = false) {
  return request.post('/admin/plan-algorithms/publish-dadi', null, { params: { force } })
}

/** 清空全部中挂历史（默认保留当期快照，不回填旧数据） */
export function clearPlanDadiHitHistory(keepCurrentSnapshots = true) {
  return request.post('/admin/plan-algorithms/clear-hit-history', null, {
    params: { keepCurrentSnapshots }
  })
}

const mockTradingSettings = {
  orderCutoffSecondsBeforeDraw: 30,
  updatedAt: ''
}

export function getTradingSettings() {
  if (USE_MOCK) return mockRequest({ ...mockTradingSettings })
  return request.get('/admin/trading-settings')
}

export function updateTradingSettings(data) {
  if (USE_MOCK) {
    Object.assign(mockTradingSettings, data, {
      updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' ')
    })
    return mockRequest({ ...mockTradingSettings })
  }
  return request.put('/admin/trading-settings', data)
}

const mockRegistrationSettings = {
  requireInviteCode: true,
  updatedAt: ''
}

export function getRegistrationSettings() {
  if (USE_MOCK) return mockRequest({ ...mockRegistrationSettings })
  return request.get('/admin/registration-settings')
}

export function updateRegistrationSettings(data) {
  if (USE_MOCK) {
    Object.assign(mockRegistrationSettings, data, {
      updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' ')
    })
    return mockRequest({ ...mockRegistrationSettings })
  }
  return request.put('/admin/registration-settings', data)
}

const mockAppDownloadSettings = {
  title: 'APP下载地址',
  downloadUrl: '',
  tip: '复制链接发送给好友即可下载安装',
  updatedAt: ''
}

export function getAppDownloadSettings() {
  if (USE_MOCK) return mockRequest({ ...mockAppDownloadSettings })
  return request.get('/admin/app-download-settings')
}

export function updateAppDownloadSettings(data) {
  if (USE_MOCK) {
    Object.assign(mockAppDownloadSettings, data, {
      updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' ')
    })
    return mockRequest({ ...mockAppDownloadSettings })
  }
  return request.put('/admin/app-download-settings', data)
}

const mockAgentSettings = {
  introTitle: '暗潮代理中心',
  introSubtitle: '推广TW5FC预售方案 · 邀请好友 · 共享收益',
  introDescription: '成为暗潮代理后，可通过专属邀请码发展用户，用户购买计划A/B方案后您将获得对应比例佣金。',
  heroBadge: '暗潮代理计划',
  pendingReviewText: '我们将在1-3个工作日内完成审核，请耐心等待',
  applySuccessMessage: '申请已提交，我们将在1-3个工作日内审核',
  inviteCodeTip: '分享给好友注册时填写，即可绑定为您的下级',
  levels: [
    { level: 1, name: '初级代理', commissionRate: 10, requirements: '通过代理申请审核', benefits: ['专属邀请码', '方案购买10%佣金'] },
    { level: 2, name: '高级代理', commissionRate: 15, requirements: '月邀请≥10人且有效订单≥5单', benefits: ['佣金提升至15%'] },
    { level: 3, name: '金牌代理', commissionRate: 20, requirements: '月邀请≥30人且有效订单≥20单', benefits: ['佣金提升至20%'] }
  ],
  assessmentRules: [
    { title: '考核周期', content: '每月1日统计上月数据，5日前完成等级评定与佣金结算。' }
  ],
  updatedAt: ''
}

export function getAgentSettings() {
  if (USE_MOCK) return mockRequest({ ...mockAgentSettings })
  return request.get('/admin/agent-settings')
}

export function updateAgentSettings(data) {
  if (USE_MOCK) {
    Object.assign(mockAgentSettings, data, {
      updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' ')
    })
    return mockRequest({ ...mockAgentSettings })
  }
  return request.put('/admin/agent-settings', data)
}
